import { useState, useEffect, useCallback } from 'react';
import { Shield, AlertTriangle, Zap, TrendingUp, Globe, RefreshCw, Link2, ExternalLink, BarChart3, Target, Activity, Brain } from 'lucide-react';
import { ScoreRing, SeverityBadge } from './SEOOSShared';
import { Button } from '@/components/ui/button';

const API = '';

export default function SEOOSWarRoom() {
  const [data, setData] = useState(null);
  const [gscStatus, setGscStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}` };

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/seoos/war-room`, { headers }).then(r => r.json()),
      fetch(`${API}/api/seoos/gsc/status`, { headers }).then(r => r.json()),
    ]).then(([wr, gs]) => { setData(wr); setGscStatus(gs); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const connectGSC = async () => {
    setConnecting(true);
    const res = await fetch(`${API}/api/seoos/gsc/connect`, { headers }).then(r => r.json());
    if (res.auth_url) window.open(res.auth_url, '_blank', 'width=600,height=700');
    setConnecting(false);
  };

  const syncGSC = async () => {
    setSyncing(true);
    await fetch(`${API}/api/seoos/gsc/sync`, { method: 'POST', headers });
    load();
    setSyncing(false);
  };

  if (loading || !data) return <div className="flex items-center justify-center h-96"><div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>;

  const { momentum, scores, alerts, gsc, quick_wins, insights, stats } = data;

  return (
    <div className="space-y-6" data-testid="seoos-war-room">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-black text-white flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <Activity className="w-5 h-5 text-red-400 animate-pulse" />SEO Savas Odasi
          </h2>
          <p className="text-xs text-slate-500">Canli SEO komuta merkezi</p>
        </div>
        <div className="flex items-center gap-2">
          {gscStatus?.connected ? (
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded border border-emerald-500/20">GSC Bagli</span>
              <Button onClick={syncGSC} disabled={syncing} size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-xs h-7">
                <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />{syncing ? 'Sync...' : 'GSC Sync'}
              </Button>
            </div>
          ) : (
            <Button onClick={connectGSC} disabled={connecting} className="bg-blue-600 hover:bg-blue-500 text-white text-xs">
              <Globe className="w-3.5 h-3.5 mr-1.5" />{connecting ? 'Baglaniyor...' : 'Google Search Console Bagla'}
            </Button>
          )}
        </div>
      </div>

      {/* MOMENTUM + SCORES */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-3 bg-gradient-to-br from-slate-900 via-purple-950/20 to-slate-900 border border-purple-500/20 rounded-2xl p-6 flex flex-col items-center justify-center shadow-[0_0_30px_rgba(147,51,234,0.08)]">
          <ScoreRing score={Math.round(momentum)} size={130} label="Momentum" />
          <p className="text-[10px] text-slate-500 mt-2">SEO Momentum Skoru</p>
        </div>
        <div className="col-span-9 grid grid-cols-5 gap-3">
          {Object.entries(scores).map(([key, val]) => {
            const labels = { technical: 'Teknik', content: 'Icerik', crawl_efficiency: 'Tarama', authority: 'Otorite', index: 'Indeks' };
            const colors = val >= 60 ? 'text-emerald-400' : val >= 40 ? 'text-amber-400' : 'text-red-400';
            return (
              <div key={key} className="bg-slate-900/40 border border-slate-800/50 rounded-xl p-4 text-center">
                <p className={`text-2xl font-black ${colors}`} style={{ fontFamily: 'var(--font-heading)' }}>{Math.round(val)}</p>
                <p className="text-[10px] text-slate-500 uppercase">{labels[key] || key}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ALERTS */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-red-400 uppercase flex items-center gap-1"><AlertTriangle className="w-4 h-4" />Canli Uyarilar</h3>
          {alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${a.level === 'critical' ? 'bg-red-500/5 border-red-500/20' : 'bg-orange-500/5 border-orange-500/20'}`}>
              <SeverityBadge severity={a.level} />
              <div className="flex-1">
                <p className="text-sm font-bold text-white">{a.title}</p>
                <p className="text-xs text-slate-400">{a.detail}</p>
              </div>
              <span className="text-[10px] text-purple-400">{a.action}</span>
            </div>
          ))}
        </div>
      )}

      {/* GSC REAL DATA */}
      {gsc && (
        <div className="bg-gradient-to-br from-blue-950/20 to-slate-900 border border-blue-500/20 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-blue-400 uppercase mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4" />Google Search Console Verileri
            <span className="text-[10px] text-slate-600 font-normal">{gsc.period}</span>
          </h3>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="text-center">
              <p className="text-2xl font-black text-blue-400">{gsc.clicks}</p>
              <p className="text-[10px] text-slate-500">Tiklamalar</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-black text-purple-400">{gsc.impressions?.toLocaleString()}</p>
              <p className="text-[10px] text-slate-500">Gosterimler</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-black text-amber-400">%{gsc.avg_ctr}</p>
              <p className="text-[10px] text-slate-500">Ort. CTR</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-black text-emerald-400">{gsc.avg_position}</p>
              <p className="text-[10px] text-slate-500">Ort. Pozisyon</p>
            </div>
          </div>

          {/* Top Queries */}
          {gsc.top_queries?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-bold text-slate-300 mb-2">En Iyi Sorgular</h4>
              <div className="space-y-1">
                {gsc.top_queries.slice(0, 5).map((q, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs py-1">
                    <span className="text-slate-500 w-4 text-right">{i + 1}</span>
                    <span className="text-slate-300 flex-1">{q.query}</span>
                    <span className="text-blue-400 w-12 text-right">{q.clicks} tik</span>
                    <span className="text-purple-400 w-16 text-right">{q.impressions} gos</span>
                    <span className="text-amber-400 w-14 text-right">%{q.ctr}</span>
                    <span className="text-emerald-400 w-10 text-right">{q.position}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* QUICK WINS + INSIGHTS */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-bold text-amber-400 uppercase mb-3 flex items-center gap-1"><Zap className="w-4 h-4" />Hizli Kazanimlar</h3>
          <div className="space-y-2">
            {quick_wins.map((w, i) => (
              <div key={i} className="bg-slate-900/40 border border-slate-800/50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-white">{w.page}</span>
                  <span className="text-[10px] text-emerald-400">{w.expected_impact}</span>
                </div>
                <p className="text-[10px] text-slate-500">Baslik: {w.title_score}/100 | Poz: ~{w.estimated_position} | CTR: %{w.estimated_ctr}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-bold text-purple-400 uppercase mb-3 flex items-center gap-1"><Brain className="w-4 h-4" />Son Icgoruler</h3>
          <div className="space-y-2">
            {insights.map((ins, i) => (
              <div key={i} className="bg-slate-900/40 border border-slate-800/50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <SeverityBadge severity={ins.severity} />
                  <span className="text-xs font-bold text-white">{ins.title}</span>
                </div>
                <p className="text-[10px] text-slate-500">{ins.explanation?.slice(0, 100)}...</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SYSTEM STATS */}
      <div className="grid grid-cols-6 gap-2">
        {[
          { label: 'Node', val: stats.total_nodes, color: 'purple' },
          { label: 'Link', val: stats.total_edges, color: 'blue' },
          { label: 'Orphan', val: stats.orphans, color: 'red' },
          { label: 'Kritik', val: stats.critical_issues, color: 'red' },
          { label: 'Yuksek', val: stats.high_issues, color: 'orange' },
          { label: 'Gorev', val: stats.open_tasks, color: 'amber' },
        ].map(s => (
          <div key={s.label} className="bg-slate-900/30 border border-slate-800/30 rounded-lg p-2 text-center">
            <p className={`text-lg font-bold text-${s.color}-400`}>{s.val}</p>
            <p className="text-[8px] text-slate-600 uppercase">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
